import { useCallback, useEffect, useRef, useState } from 'react';

import { useBizyUserConversation } from '../../../queries/bizy-conversations';
import { useBizyMarkNotificationsSeenMutation, useBizyUnreadNotifications } from '../../../queries/bizy-notifications';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { useHasBizyNotification, useIsUnreadBizyObservedMoment, useUnreadBizyConversationId, useUnreadBizyPersonId } from './use-has-bizy-notification';
import { usePeopleBrowserEnabled } from './use-people-browser-enabled';

interface Options {
	/**
	 * Imperative callback that swaps the chat panel to the given conversation
	 * id. Wired to `useBizySidePanelChat().loadConversation` from the caller.
	 */
	loadConversation: (conversationId: string) => void;
	/**
	 * The chat view's currently-loaded conversation id. The hook treats this
	 * (NOT the react-query data cache) as the "the swap actually happened"
	 * signal. Without it, a cached conversation response would resolve
	 * synchronously from react-query and `markSeen` could fire before the
	 * chat view actually swapped — which still consumes the notification
	 * even if the swap later fails for an unrelated reason.
	 */
	currentConversationId: string | undefined;
	/**
	 * Optional secondary auto-load condition. The hook always auto-loads on
	 * `lastTrigger === 'notification_peek'`. Pass true to ALSO auto-load on
	 * other open paths (e.g., the panel is in empty state after a page
	 * refresh — you want the proactive conversation to land instead of an
	 * empty greeting). Pass false (default) to leave non-peek opens alone.
	 */
	autoLoadOnOpen?: boolean;
}

/**
 * Coordinates the proactive-notification UX inside the side panel.
 *
 * Behavior:
 *   - When the user opens the panel via the peek (`lastTrigger === 'notification_peek'`),
 *     the peek is the explicit acknowledgement, so we auto-load the
 *     conversation. The user lands directly on Bizy's message.
 *   - For any other open path (toggle, already-open + new notification), we
 *     leave the chat in its current state and let the empty-state greeting
 *     do its job. The user clicks the wave to engage, which loads the
 *     conversation.
 *
 * `markSeen` only fires AFTER the chat view actually swaps to the target
 * conversation. If the load fails (network blip, 404, auth), we leave the
 * notification unread AND release the per-conversation engagement guard so
 * the user can retry the same conversation in the same open session.
 */
export function useLoadProactiveConversationOnOpen({ loadConversation, currentConversationId, autoLoadOnOpen = false }: Options) {
	const isOpen = useBizySidePanelStore((s) => s.isOpen);
	const lastTrigger = useBizySidePanelStore((s) => s.lastTrigger);
	const engageRequestVersion = useBizySidePanelStore((s) => s.engageRequestVersion);
	const hasUnread = useHasBizyNotification();
	const conversationId = useUnreadBizyConversationId();
	// Post-BIZY-633, the latest unread can be a People Browser observed moment
	// with no conversation to open. In that case we focus the sidebar entry
	// instead of attempting to load a nonexistent conversation.
	//
	// Only treat it as an observed moment when the People Browser is actually
	// enabled — if the backend `kind` goes live while the flags are off, the
	// sidebar isn't rendered, so focusing an entry would be a dead end and
	// leave the unread stuck. Falling through to the conversation path lets the
	// legacy null-conversation handling clear it instead.
	const peopleBrowserEnabled = usePeopleBrowserEnabled();
	const isObservedMoment = useIsUnreadBizyObservedMoment() && peopleBrowserEnabled;
	const unreadPersonId = useUnreadBizyPersonId();
	const focusPerson = useBizySidePanelStore((s) => s.focusPerson);
	// `isFetching` lets us tell a transient null (a recent scoped markSeen
	// optimistically nulled latestConversationId and the refetch is in
	// flight) from a persistent null (legacy notification with no
	// chat_history_id linkage). The mark-seen mutation's onSuccess does
	// setQueryData → invalidateQueries synchronously, so the moment
	// observers see latestConversationId === null from the optimistic
	// update they also see isFetching === true.
	const { isFetching: isFetchingUnread } = useBizyUnreadNotifications();
	const { mutate: markSeen } = useBizyMarkNotificationsSeenMutation();

	// Snapshot of autoLoadOnOpen captured at the close→open transition.
	// We auto-load only if the snapshot is true, not the live prop. That
	// way a mid-session flip (e.g. user clicks "New chat" while an unread
	// is pending → isEmptyState becomes true → autoLoadOnOpen prop flips
	// to true) can't re-trigger an auto-load against the user's intent.
	// We also CAN'T just key off "first effect run with isOpen=true",
	// because hasUnread is async and usually resolves on a later effect
	// run — so the snapshot has to outlive multiple effect passes within
	// the same open session.
	const autoLoadOnOpenSnapshotRef = useRef(false);
	// Whether any explicit engagement (peek-engage, engageWithGreeting,
	// acknowledgeUnread, legacy markSeen({})) has happened this open
	// session. Distinct from `passiveAutoLoadFiredThisOpenRef` because an
	// engagement-failure rollback should NOT also un-fire a passive load
	// that has already swapped the chat view — that event is observable
	// to the user and can't be unwound.
	const engagementSuppressesPassiveRef = useRef(false);
	// Per-session dedup for the legacy notification path (markSeen({})
	// fired when conversationId is persistently null). Without this, a
	// burst of acknowledgeUnread calls (e.g. one per keystroke) would
	// fire one /seen request per call.
	const legacyMarkAllFiredThisSessionRef = useRef(false);
	// Same pattern for lastTrigger. The store's lastTrigger sticks at
	// 'notification_peek' for the rest of the session after a peek-open;
	// without snapshotting, a non-peek session that flipped lastTrigger
	// for any reason would inherit peek semantics. We only auto-engage
	// when the session was actually peek-opened.
	const peekOpenedSnapshotRef = useRef(false);
	// One-shot guard: once the peek branch actually engages this open
	// session, it never engages again — even if a new notification
	// surfaces via window-focus refetch and changes `conversationId`.
	// Without this, the user would get yanked out of the conversation
	// they were reading. Subsequent unread notifications follow the same
	// in-chat-engage-by-click semantics as toggle-opened sessions.
	const peekFiredThisOpenRef = useRef(false);
	const wasOpenRef = useRef(false);
	// One-shot guard for the passive `autoLoadOnOpen` branch only.
	// Active engagement (peek, engageWithGreeting, acknowledgeUnread) uses
	// the per-conversation set below so a NEW notification mid-session can
	// still fire markSeen for its own conversation id.
	const passiveAutoLoadFiredThisOpenRef = useRef(false);

	// Per-conversation engagement guard. Replaces a prior single boolean
	// "fired this open" so that:
	//   - Bursts of engagement signals (keystrokes, repeated peek clicks)
	//     for the same conversation are deduped within a session.
	//   - A NEW notification arriving mid-session (different conversationId)
	//     can re-trigger active engagement without requiring a panel close.
	//   - Failure to load lets us release the entry so the user can retry
	//     the same conversation.
	const engagedConversationsRef = useRef<Set<string>>(new Set());

	// Stable refs so the main effect's dep array reflects only the logical
	// inputs (open state, trigger, unread, conversation id) — not the
	// identity of caller-provided callbacks. If a future caller ever
	// passes a non-stable `loadConversation`, the effect won't spuriously
	// re-fire on every render. We always read `.current` so behavior
	// tracks the latest props.
	const loadConversationRef = useRef(loadConversation);
	loadConversationRef.current = loadConversation;
	const currentConversationIdRef = useRef(currentConversationId);
	currentConversationIdRef.current = currentConversationId;
	const isFetchingUnreadRef = useRef(isFetchingUnread);
	isFetchingUnreadRef.current = isFetchingUnread;

	// Conversation id we're waiting to mark seen once the chat view actually
	// swaps to it. Subscribing to the same react-query key the chat hook
	// uses dedupes the network call AND lets us observe load failures.
	const [pendingMarkSeenConvId, setPendingMarkSeenConvId] = useState<string | null>(null);
	const { isError: pendingLoadFailed, errorUpdatedAt: pendingErrorUpdatedAt } = useBizyUserConversation(pendingMarkSeenConvId ?? undefined);
	// Timestamp captured at the moment we set up a new pending mark-seen.
	// React-query keeps a failed query's `isError: true` cached after the
	// first failure, so a retry attempt would see the stale error on the
	// very first render and prematurely clear the pending state — without
	// this baseline, markSeen would never fire even if the retry succeeds.
	// We only treat `isError` as authoritative when `errorUpdatedAt` has
	// advanced past the baseline, i.e. a fresh failure for the new attempt.
	const errorBaselineRef = useRef<number>(0);

	useEffect(() => {
		if (!pendingMarkSeenConvId) return;

		// Success criterion is the chat view's own conversation id, not the
		// react-query cache. Cached responses come back synchronously, and
		// observing them as "loaded" would fire markSeen before the swap
		// effect inside the chat hook actually ran — defeating the
		// "only mark after the user can see Bizy's message" guarantee.
		if (currentConversationId === pendingMarkSeenConvId) {
			const convId = pendingMarkSeenConvId;
			markSeen(
				{ conversationId: convId },
				{
					// Release the per-conversation engagement guard if the
					// mark-seen request itself fails (network blip, 500).
					// The user is already on the conversation, so re-
					// engagement should retry the markSeen — without this,
					// the indicator would stay on with no in-session
					// recovery path. We don't touch
					// `engagementSuppressesPassiveRef` here: the user is
					// on the conversation regardless of whether /seen
					// succeeds, so we don't want a stale passive-auto-load
					// to fire and bounce them somewhere else.
					onError: () => {
						engagedConversationsRef.current.delete(convId);
					},
				}
			);
			setPendingMarkSeenConvId(null);
			return;
		}

		// Load failed — drop the pending markSeen AND release the engagement
		// guard so the user can retry the same conversation without having
		// to close and reopen the panel. Gated on `errorUpdatedAt` so a
		// cached error from a prior attempt doesn't abort the retry before
		// it's had a chance to land.
		if (pendingLoadFailed && pendingErrorUpdatedAt > errorBaselineRef.current) {
			engagedConversationsRef.current.delete(pendingMarkSeenConvId);
			setPendingMarkSeenConvId(null);
		}
	}, [pendingMarkSeenConvId, currentConversationId, pendingLoadFailed, pendingErrorUpdatedAt, markSeen]);

	// Reset session-scoped state on close so the next open with a new
	// notification can engage fresh.
	useEffect(() => {
		if (!isOpen) {
			engagedConversationsRef.current = new Set();
			engagementSuppressesPassiveRef.current = false;
			legacyMarkAllFiredThisSessionRef.current = false;
			setPendingMarkSeenConvId(null);
		}
	}, [isOpen]);

	const engageWithConversation = useCallback(
		(targetId: string | null) => {
			if (!targetId) {
				// targetId is null but hasUnread is true. Two distinct cases:
				//   - Transient null (isFetching=true): a recent scoped
				//     markSeen just optimistically nulled latestConversationId
				//     and the refetch hasn't landed. Falling back to
				//     markSeen({}) would silently consume any other linked
				//     unread notifications that the refetch is about to roll
				//     forward.
				//   - Persistent null (isFetching=false, steady state): a
				//     legacy notification with no chat_history_id linkage.
				//     This is the only path that ever clears the indicator
				//     for those — without it, the user is stuck with the
				//     dot on forever.
				if (isFetchingUnreadRef.current) return;
				// Per-session dedup so a burst of engagement signals
				// (keystrokes etc.) doesn't fire one /seen request per
				// call.
				if (legacyMarkAllFiredThisSessionRef.current) return;
				legacyMarkAllFiredThisSessionRef.current = true;
				engagementSuppressesPassiveRef.current = true;
				markSeen(
					{},
					{
						// Release the dedup + passive-suppression guards
						// on failure so the user has an in-session retry
						// path. We deliberately don't touch
						// `passiveAutoLoadFiredThisOpenRef` — if a passive
						// load already fired earlier this session, that
						// event is observable to the user (the chat view
						// swapped) and can't be unwound by this rollback.
						onError: () => {
							legacyMarkAllFiredThisSessionRef.current = false;
							engagementSuppressesPassiveRef.current = false;
						},
					}
				);
				return;
			}
			if (engagedConversationsRef.current.has(targetId)) return;
			engagedConversationsRef.current.add(targetId);
			// If the chat view is already showing this conversation (e.g. the
			// autoLoadOnOpen branch already loaded it passively), skip the
			// redundant load — the deferred markSeen effect will see
			// `currentConversationId === pendingMarkSeenConvId` immediately
			// and fire on the next pass.
			if (currentConversationIdRef.current !== targetId) {
				loadConversationRef.current(targetId);
			}
			// Any explicit engagement consumes the session's "passive
			// auto-load slot". Without this, a peek-engaged session that
			// also had `autoLoadOnOpen=true` (empty state at open) would
			// fire the passive branch when the post-mark refetch rolled
			// `latestConversationId` forward to a remaining unread —
			// yanking the user out of the conversation they just engaged
			// with. Once any engagement has happened this session, passive
			// auto-load is moot: the user is already on a proactive
			// conversation. Distinct from `passiveAutoLoadFiredThisOpenRef`
			// (which tracks whether the passive load itself has fired) so
			// rollback on engagement failure doesn't un-fire a real
			// passive load that already swapped the chat view.
			engagementSuppressesPassiveRef.current = true;
			// Capture before flipping pending so the deferred effect can
			// distinguish a fresh failure for this attempt from a cached
			// error left over from a prior failed attempt at the same id.
			errorBaselineRef.current = Date.now();
			setPendingMarkSeenConvId(targetId);
		},
		[markSeen]
	);

	useEffect(() => {
		if (!isOpen) {
			wasOpenRef.current = false;
			autoLoadOnOpenSnapshotRef.current = false;
			peekOpenedSnapshotRef.current = false;
			peekFiredThisOpenRef.current = false;
			passiveAutoLoadFiredThisOpenRef.current = false;
			return;
		}

		// Capture conditions at the moment the panel transitions to open.
		// Subsequent effect runs within the same open session use the
		// snapshot, not the live prop.
		if (!wasOpenRef.current) {
			wasOpenRef.current = true;
			autoLoadOnOpenSnapshotRef.current = autoLoadOnOpen;
			peekOpenedSnapshotRef.current = lastTrigger === 'notification_peek';
		}

		if (!hasUnread) return;

		// Two auto-paths, with different acknowledgement semantics:
		//   - peek-opened snapshot: explicit click on the unread indicator.
		//     Load AND mark seen — the user told us they saw it. Fires at
		//     most once per open session: a new notification surfacing
		//     mid-session via window-focus refetch must NOT yank the user
		//     out of whatever they were reading. The transient-vs-
		//     persistent null disambiguation lives inside
		//     engageWithConversation; we set the fire guard before calling
		//     so transient null + later resolution still engages cleanly
		//     (engageWithConversation no-ops on null + isFetching, so the
		//     guard release in that path lets the next effect run try
		//     again — see below).
		//   - autoLoadOnOpen snapshot (refresh-into-empty-state): passive.
		//     Load the conversation so the user lands on Bizy's message, but
		//     DO NOT mark seen. The in-chat waving Bizy will dance until the
		//     user actually engages (hover stops the dance; click/type marks
		//     seen). One-shot per open session, and only when there's a
		//     conversation to load (legacy notifications have nothing to
		//     surface — the user can clear them via engageWithGreeting).
		if (peekOpenedSnapshotRef.current && !peekFiredThisOpenRef.current) {
			// Observed-moment peek: there is no conversation to load — open the
			// People Browser and focus the entry instead. Marking the moment
			// seen happens when the user interacts with the sidebar entry (it
			// uses the notification-scoped people seen mutation), so we don't
			// fire the conversation-scoped markSeen here.
			if (isObservedMoment) {
				peekFiredThisOpenRef.current = true;
				focusPerson(unreadPersonId);
				return;
			}
			// Engage if there's something to engage with. For transient null
			// (refetch in flight) we leave the guard false so the effect
			// re-runs when conversationId or isFetchingUnread resolves. For
			// persistent null (legacy) engageWithConversation falls back to
			// markSeen({}) — count that as the peek branch firing. Read
			// isFetchingUnread directly (not via ref) so its true→false
			// transition triggers a re-run of this effect; without that,
			// a refetch landing while conversationId stayed null (e.g. the
			// remaining unread is also legacy) would leave the indicator
			// stuck until panel close.
			if (conversationId) {
				peekFiredThisOpenRef.current = true;
				engageWithConversation(conversationId);
			} else if (!isFetchingUnread) {
				peekFiredThisOpenRef.current = true;
				engageWithConversation(null);
			}
			return;
		}
		if (autoLoadOnOpenSnapshotRef.current && !passiveAutoLoadFiredThisOpenRef.current && !engagementSuppressesPassiveRef.current && conversationId) {
			passiveAutoLoadFiredThisOpenRef.current = true;
			loadConversationRef.current(conversationId);
		}
	}, [isOpen, lastTrigger, autoLoadOnOpen, hasUnread, conversationId, isFetchingUnread, engageWithConversation, isObservedMoment, unreadPersonId, focusPerson]);

	// Click-driven engagement signal: bumped by the store when the user
	// clicks Ask Bizy with an unread, regardless of whether the panel was
	// already open. The peek branch above only fires once per close→open
	// transition (snapshot pattern), so a user who Ask-Bizy-clicks while
	// the panel is already open would otherwise see nothing — they'd have
	// to open the dropdown and pick the conversation manually. This
	// effect closes that gap. We dedupe on the version so the handler
	// only runs on actual bumps, not on stale renders.
	const lastEngageRequestRef = useRef(engageRequestVersion);
	useEffect(() => {
		if (engageRequestVersion === lastEngageRequestRef.current) return;
		lastEngageRequestRef.current = engageRequestVersion;
		if (!hasUnread) return;
		// Observed-moment unread: focus the People Browser entry rather than
		// engaging a (nonexistent) conversation.
		if (isObservedMoment) {
			focusPerson(unreadPersonId);
			return;
		}
		// Per-conversation guard inside engageWithConversation prevents
		// double-fire if the peek branch already engaged this conversation
		// in the same render pass.
		engageWithConversation(conversationId);
	}, [engageRequestVersion, hasUnread, conversationId, engageWithConversation, isObservedMoment, unreadPersonId, focusPerson]);

	const engageWithGreeting = useCallback(() => {
		if (!hasUnread) return;
		// Observed-moment unread: the waving Bizy click should focus the People
		// Browser entry, not engage a (nonexistent) conversation.
		if (isObservedMoment) {
			focusPerson(unreadPersonId);
			return;
		}
		engageWithConversation(conversationId);
	}, [hasUnread, isObservedMoment, unreadPersonId, focusPerson, conversationId, engageWithConversation]);

	// Lighter-weight engagement signal: marks the unread proactive
	// notification as seen WITHOUT loading its conversation. Use this when
	// the user has clearly engaged with Bizy in some other way (typed in
	// the input, clicked the Ask Bizy button) and they don't need the
	// proactive conversation switched in front of whatever they were doing.
	//
	// Deduped per conversation via engagedConversationsRef so callers like
	// the chat input's onChange (which fires per keystroke) only produce one
	// markSeen request per session per conversation, instead of a burst
	// while the optimistic update is in flight.
	const acknowledgeUnread = useCallback(() => {
		if (!hasUnread) return;
		// Observed-moment unread has no conversation — don't fire the legacy
		// conversation-scoped markSeen fallback. Its seen/skip state is managed
		// by the sidebar entry's own mutations (people seen/skip), so a generic
		// chat-input ack is a no-op here.
		if (isObservedMoment) return;
		if (!conversationId) {
			// Same transient-vs-persistent split as engageWithConversation.
			// Defer during the optimistic-update refetch window; otherwise
			// treat null as a legacy notification and clear all unopened.
			// Per-session dedup (legacyMarkAllFiredThisSessionRef) keeps
			// keystroke bursts from each firing /seen.
			if (isFetchingUnread) return;
			if (legacyMarkAllFiredThisSessionRef.current) return;
			legacyMarkAllFiredThisSessionRef.current = true;
			engagementSuppressesPassiveRef.current = true;
			markSeen(
				{},
				{
					onError: () => {
						legacyMarkAllFiredThisSessionRef.current = false;
						engagementSuppressesPassiveRef.current = false;
					},
				}
			);
			return;
		}
		if (engagedConversationsRef.current.has(conversationId)) return;
		engagedConversationsRef.current.add(conversationId);
		// Explicit engagement (typing, ask-bizy click while open, …)
		// suppresses subsequent passive auto-load for the same reason
		// engageWithConversation does.
		engagementSuppressesPassiveRef.current = true;
		// Per-call onError releases the per-conversation guard so the user
		// can retry within the same session if the request fails. Without
		// this, the next keystroke / click for this same conversation
		// would dedupe to a no-op and the indicator would stay on with no
		// in-session recovery path. We deliberately don't unwind
		// `engagementSuppressesPassiveRef` — once any engagement happened,
		// even a failed one, the user has shown intent and a passive
		// auto-load to a different conversation would be jarring.
		markSeen(
			{ conversationId },
			{
				onError: () => {
					engagedConversationsRef.current.delete(conversationId);
				},
			}
		);
	}, [hasUnread, isObservedMoment, conversationId, isFetchingUnread, markSeen]);

	return {
		showGreeting: hasUnread,
		engageWithGreeting,
		acknowledgeUnread,
	};
}
